import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import SnackbarInfo from '../common/snackbar-info.component';
import { updateDataById } from '../../services/videos.service';
import Distributors from '../form-elements/distributors.component';
import { maxLengthValidation } from '../../utils/validations.util';

const VideoDistributorMonetization = ({
  videoDetail,
  type,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
  } = useForm({
    defaultValues: {
      distributor:
        videoDetail.distributorId && videoDetail.distributorName
          ? { id: videoDetail.distributorId, name: videoDetail.distributorName }
          : '' || '',
      distributorSku: videoDetail.distributorSku || '',
      distributorSku2: videoDetail.distributorSku2 || '',
      contractNo: videoDetail.contractNo || '',
      studio: videoDetail.studio || '',
    },
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      ...data,
      distributor: data?.distributor?.id || null,
      type,
    };
    updateDataById(videoDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Owner data updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      <form
        id="distributor-monetization-form"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="distributor"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Distributors
                  id="distributor"
                  name="distributor"
                  label="Distributor"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.distributor?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="studio"
              control={control}
              rules={maxLengthValidation(55)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Label"
                  type="text"
                  error={!!errors.studio}
                  helperText={errors?.studio?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="distributorSku"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Distributor SKU"
                  type="text"
                  error={!!errors.distributorSku}
                  helperText={errors?.distributorSku?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="distributorSku2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Distributor SKU2"
                  type="text"
                  error={!!errors.distributorSku2}
                  helperText={errors?.distributorSku2?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <TextField
              label="Royality"
              type="text"
              fullWidth
              variant="filled"
              value={videoDetail.distributorRoyality || 0}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="contractNo"
              control={control}
              rules={maxLengthValidation(100)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Contract No"
                  type="text"
                  error={!!errors.contractNo}
                  helperText={errors?.contractNo?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="distributor-monetization-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted}
        >
          Save Owner
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

VideoDistributorMonetization.propTypes = {
  videoDetail: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default VideoDistributorMonetization;
